
import React, { useState } from 'react';
import { MemberModel, UserRole, BranchModel, SiteSettingModel, StudentGuardian, GRADE_TYPES, CompanyName } from '../../models';
import { X, UserPlus, Mail, GraduationCap, Phone, Users, Smartphone, ShieldCheck, Building2, MapPin, ArrowLeft, Save, Search, Plus, Trash2 } from 'lucide-react';
import NeisSearchModal, { NeisSchoolData } from '../../components/NeisSearchModal';

interface PublicRegistrationProps {
    onBack: () => void;
    onSubmit: (member: MemberModel) => void;
    branches: BranchModel[];
    siteSettings: SiteSettingModel;
    initialEmail?: string;
    initialName?: string;
}

const ROLE_LABELS: Record<UserRole, { label: string; icon: any; desc: string }> = {
    super_admin: { label: '최고관리자', icon: ShieldCheck, desc: '시스템 전체 관리' },
    manager: { label: '매니저', icon: ShieldCheck, desc: '지점 관리' },
    staff: { label: '직원', icon: Users, desc: '지점 운영 및 교육 상담' },
    teacher: { label: '선생님', icon: Users, desc: '학습 관리 및 지도' },
    student: { label: '학생', icon: GraduationCap, desc: '개인별 맞춤 학습 진행' },
    parent: { label: '학부모', icon: Users, desc: '자녀 학습 모니터링' },
};

const PublicRegistration: React.FC<PublicRegistrationProps> = ({
    onBack, onSubmit, branches, siteSettings, initialEmail = '', initialName = ''
}) => {
    const [isSchoolModalOpen, setIsSchoolModalOpen] = useState(false);

    // Form State
    const [role, setRole] = useState<UserRole>('student');
    const branchId = 'main';

    const [name, setName] = useState(initialName);
    const [gender, setGender] = useState<'남' | '여' | ''>('');
    const [phone, setPhone] = useState('');
    const [address, setAddress] = useState('');
    const [email, setEmail] = useState(initialEmail); // User Email (not guardian)

    // Student specific
    const [schoolName, setSchoolName] = useState('');
    const [grade, setGrade] = useState('');

    // NEIS Data
    const [neisData, setNeisData] = useState<{
        code: string;
        type: string;
        officeCode: string;
        officeName: string;
    } | null>(null);

    // Guardians
    const [guardians, setGuardians] = useState<StudentGuardian[]>([
        { relation: '부', name: '', phone: '', email: '' }
    ]);

    const isStudent = role === 'student';

    const handleAddGuardian = () => {
        setGuardians([...guardians, { relation: '부', name: '', phone: '', email: '' }]);
    };

    const handleRemoveGuardian = (index: number) => {
        setGuardians(guardians.filter((_, i) => i !== index));
    };

    const handleGuardianChange = (index: number, field: keyof StudentGuardian, value: string) => {
        const newGuardians = [...guardians];
        newGuardians[index] = { ...newGuardians[index], [field]: value };
        setGuardians(newGuardians);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        // Validations
        if (!name) return alert('이름을 입력해주세요.');
        if (!gender) return alert('성별을 선택해주세요.');
        if (!phone || phone.length !== 11) return alert('올바른 연락처를 입력해주세요.');
        if (!address) return alert('주소를 입력해주세요.');

        if (isStudent) {
            if (!schoolName) return alert('학교를 선택해주세요.');
            if (!grade) return alert('학년을 선택해주세요.');
            if (guardians.length === 0) return alert('보호자 정보를 최소 1명 입력해주세요.');

            for (const g of guardians) {
                if (!g.phone || g.phone.length !== 11) return alert('모든 보호자의 올바른 연락처를 입력해주세요.');
            }
        }

        onSubmit({
            id: crypto.randomUUID(),
            name,
            role,
            branchId,
            gender,
            phone,
            address,
            email,
            joinedAt: Date.now(),

            // Default Access Policy for new signups
            dueAt: Date.now() + (30 * 24 * 60 * 60 * 1000), // Legacy check
            status: 'active', // Default active
            enrollmentStatus: isStudent ? '재원' : undefined,
            guardians: isStudent ? guardians : undefined,
            accessPolicy: {
                type: !isStudent ? 'PERIOD' : 'UNLIMITED',
                // Explicitly set null if not used, instead of undefined
                dueAt: !isStudent ? Date.now() + (30 * 24 * 60 * 60 * 1000) : null
            },

            // Student Info
            grade,
            schoolName,
            neisSchoolCode: neisData?.code,
            neisSchoolType: neisData?.type,
            neisOfficeCode: neisData?.officeCode,
            neisOfficeName: neisData?.officeName,

            // Legacy Mappings
            parentRole: guardians[0]?.relation as any,
            parentPhone: guardians[0]?.phone,
            parentEmail: guardians[0]?.email,
        });
    };

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col items-center py-12 px-4 sm:px-6 lg:px-8 relative">
            <button
                onClick={onBack}
                className="absolute top-4 left-4 md:top-8 md:left-8 z-10 flex items-center gap-2 text-slate-400 hover:text-slate-800 transition-colors font-bold text-xs uppercase tracking-widest group"
            >
                <div className="w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center group-hover:bg-slate-50 transition-all">
                    <ArrowLeft size={16} />
                </div>
                뒤로가기
            </button>
            <div className="w-full max-w-2xl bg-white rounded-[2.5rem] shadow-2xl overflow-hidden border border-slate-100">
                <div className="p-6 md:p-10 border-b border-slate-100 bg-indigo-600 text-white flex flex-col items-center text-center space-y-4">
                    <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-md">
                        <UserPlus size={32} />
                    </div>
                    <div>
                        <h3 className="text-3xl font-black tracking-tight">회원 가입 및 정보 등록</h3>
                        <p className="text-indigo-100 font-medium mt-1">
                            상세 정보를 입력해 주세요
                        </p>
                    </div>
                </div>

                {(
                    <form onSubmit={handleSubmit} className="p-6 md:p-10 space-y-6 md:space-y-8 animate-in slide-in-from-right-8 duration-300">
                        {/* Common Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="space-y-4">
                                <label className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                    <ShieldCheck size={14} className="text-indigo-500" /> 가입 유형 <span className="text-rose-500">*</span>
                                </label>
                                <select
                                    value={role}
                                    onChange={(e) => setRole(e.target.value as UserRole)}
                                    className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-5 py-4 text-base font-bold focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all appearance-none cursor-pointer"
                                    required
                                >
                                    <option value="student">학생 (Student)</option>
                                    <option value="parent">학부모 (Parent)</option>
                                    <option value="teacher">선생님 (Teacher)</option>
                                    <option value="super_admin">최고관리자 (Super Admin)</option>
                                </select>
                            </div>

                            <div className="space-y-4">
                                <label className="text-xs font-black text-slate-400 uppercase tracking-widest">이름 <span className="text-rose-500">*</span></label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-5 py-4 text-base font-bold focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-sans"
                                    placeholder="이름 입력"
                                    required
                                />
                            </div>

                            <div className="space-y-4">
                                <label className="text-xs font-black text-slate-400 uppercase tracking-widest">성별 <span className="text-rose-500">*</span></label>
                                <div className="flex gap-2 h-[58px]">
                                    {(['남', '여'] as const).map((g) => (
                                        <button
                                            key={g}
                                            type="button"
                                            onClick={() => setGender(g)}
                                            className={`flex-1 rounded-2xl text-sm font-black border-2 transition-all ${gender === g
                                                ? 'bg-slate-800 border-slate-800 text-white shadow-lg'
                                                : 'bg-white border-slate-100 text-slate-400 hover:border-slate-200'
                                                }`}
                                        >
                                            {g}자
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="space-y-4">
                                <label className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                    <Smartphone size={14} /> 연락처 <span className="text-rose-500">*</span>
                                </label>
                                <input
                                    type="tel"
                                    value={phone}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/[^0-9]/g, '');
                                        if (val.length <= 11) setPhone(val);
                                    }}
                                    className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-5 py-4 text-base font-bold focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all"
                                    placeholder="01000000000"
                                    required
                                />
                            </div>

                            <div className="space-y-4">
                                <label className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                    <MapPin size={14} /> 주소 <span className="text-rose-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={address}
                                    onChange={(e) => setAddress(e.target.value)}
                                    className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-5 py-4 text-base font-bold focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all"
                                    placeholder="도로명 혹은 지번 주소"
                                    required
                                />
                            </div>
                        </div>

                        {/* Student Specific */}
                        {isStudent && (
                            <>
                                <div className="h-[1px] bg-slate-100"></div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <div className="space-y-4">
                                        <label className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                            <Building2 size={14} /> 학교명 <span className="text-rose-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="text"
                                                value={schoolName}
                                                readOnly
                                                onClick={() => setIsSchoolModalOpen(true)}
                                                className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-5 py-4 text-base font-bold focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all cursor-pointer"
                                                placeholder="학교 검색하기"
                                                required
                                            />
                                            <Search className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={20} />
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <label className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                            <GraduationCap size={14} /> 학년 <span className="text-rose-500">*</span>
                                        </label>
                                        <select
                                            value={grade}
                                            onChange={(e) => setGrade(e.target.value)}
                                            className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-5 py-4 text-base font-bold focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all appearance-none"
                                            required
                                        >
                                            <option value="" disabled>학년 선택</option>
                                            {(siteSettings.grades && siteSettings.grades.length > 0 ? siteSettings.grades : GRADE_TYPES).map(g => <option key={g} value={g}>{g}</option>)}
                                        </select>
                                    </div>
                                </div>

                                {/* Guardians List */}
                                <div className="bg-indigo-50/50 p-6 md:p-8 rounded-[2.5rem] border border-indigo-100 space-y-6">
                                    <div className="flex items-center justify-between">
                                        <h4 className="text-xs font-black text-indigo-600 uppercase tracking-widest flex items-center gap-2">
                                            <Users size={16} /> 학부모 정보 ({guardians.length}) <span className="text-rose-500">*</span>
                                        </h4>
                                        <button
                                            type="button"
                                            onClick={handleAddGuardian}
                                            className="text-[10px] font-bold bg-white text-indigo-600 border border-indigo-200 px-3 py-1.5 rounded-lg hover:bg-white transition-colors flex items-center gap-1 shadow-sm"
                                        >
                                            <Plus size={12} /> 추가
                                        </button>
                                    </div>

                                    <div className="space-y-4">
                                        {guardians.map((g, idx) => (
                                            <div key={idx} className="bg-white p-4 rounded-2xl border border-indigo-100 shadow-sm relative">
                                                {guardians.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveGuardian(idx)}
                                                        className="absolute top-4 right-4 text-slate-300 hover:text-rose-500"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                )}
                                                <div className="grid grid-cols-2 gap-4 mb-4">
                                                    <div className="space-y-2">
                                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">관계</label>
                                                        <div className="flex gap-1 h-10">
                                                            {['부', '모', '기타'].map(r => (
                                                                <button
                                                                    key={r}
                                                                    type="button"
                                                                    onClick={() => handleGuardianChange(idx, 'relation', r)}
                                                                    className={`flex-1 rounded-lg text-[10px] font-black border transition-all ${g.relation === r ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-slate-50 border-slate-100 text-slate-500'}`}
                                                                >
                                                                    {r}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    <div className="space-y-2">
                                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">이름</label>
                                                        <input
                                                            type="text"
                                                            value={g.name}
                                                            onChange={(e) => handleGuardianChange(idx, 'name', e.target.value)}
                                                            className="w-full h-10 bg-slate-50 border-slate-100 rounded-lg px-3 text-sm font-bold outline-none border focus:border-indigo-500"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                    <div className="space-y-2">
                                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">연락처 <span className="text-rose-500">*</span></label>
                                                        <input
                                                            type="tel"
                                                            value={g.phone}
                                                            onChange={(e) => {
                                                                const val = e.target.value.replace(/[^0-9]/g, '');
                                                                if (val.length <= 11) handleGuardianChange(idx, 'phone', val);
                                                            }}
                                                            className="w-full h-10 bg-slate-50 border-slate-100 rounded-lg px-3 text-sm font-bold outline-none border focus:border-indigo-500"
                                                            placeholder="01012345678"
                                                        />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">이메일</label>
                                                        <input
                                                            type="email"
                                                            value={g.email}
                                                            onChange={(e) => handleGuardianChange(idx, 'email', e.target.value)}
                                                            className="w-full h-10 bg-slate-50 border-slate-100 rounded-lg px-3 text-sm font-bold outline-none border focus:border-indigo-500"
                                                            placeholder="mail@example.com"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}

                        <button
                            type="submit"
                            className="w-full bg-indigo-600 text-white py-6 rounded-[2rem] font-black text-xl shadow-2xl shadow-indigo-100 hover:bg-indigo-700 active:scale-95 transition-all flex items-center justify-center gap-3 mt-4"
                        >
                            <Save size={24} />
                            회원 가입 완료
                        </button>
                    </form>
                )}
            </div>

            <p className="mt-12 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">
                {CompanyName} MEMBERSHIP REGISTRATION SYSTEM
            </p>

            {
                isSchoolModalOpen && (
                    <NeisSearchModal
                        onClose={() => setIsSchoolModalOpen(false)}
                        onSelect={(school) => {
                            setSchoolName(school.SCHUL_NM);
                            setNeisData({
                                code: school.SD_SCHUL_CODE,
                                type: school.SCHUL_KND_SC_NM,
                                officeCode: school.ATPT_OFCDC_SC_CODE,
                                officeName: school.ATPT_OFCDC_SC_NM
                            });
                            setIsSchoolModalOpen(false);
                        }}
                    />
                )
            }
        </div >
    );
};

export default PublicRegistration;
